import { useEffect } from "react";
import { ColumnData } from "@/entities/dataTables/types";
import { ViewDataConfiguration } from "@/entities/views/types";
import { reconcileColumnsStateWithDefaults } from "@/entities/dataTables/helpers";

// Keeps a table page's columnsState in step with the current defaults and the
// selected view. Runs on every columnsState change, not once: activating a view
// resets columnsState to that view's saved columns (DataTableToolbar), which is
// empty when it has none, and a one-shot seed never picks that back up — leaving
// the table without columns the Display popover still lists.
// An effect rather than a useMemo because columnsState is user-mutable state the
// toolbar, picker and DataTable all write to — not derived data this hook owns.
// The identity gate below relies on reconcileColumnsStateWithDefaults returning
// its input when there is nothing to change.
export const useReconciledColumnsState = ({
  columnsState,
  setColumnsState,
  defaultColumnsState,
  selectedView,
  flagGatedColumnIds,
  isReady
}: {
  columnsState: ColumnData[];
  setColumnsState: React.Dispatch<React.SetStateAction<ColumnData[]>>;
  defaultColumnsState: ColumnData[];
  selectedView: ViewDataConfiguration | undefined;
  flagGatedColumnIds: readonly string[];
  isReady: boolean;
}) => {
  useEffect(() => {
    if (!isReady) return;
    const reconciledColumns = reconcileColumnsStateWithDefaults(
      columnsState,
      defaultColumnsState,
      selectedView,
      flagGatedColumnIds
    );
    if (reconciledColumns !== columnsState) {
      setColumnsState(reconciledColumns);
    }
  }, [
    columnsState,
    setColumnsState,
    defaultColumnsState,
    selectedView,
    flagGatedColumnIds,
    isReady
  ]);
};
